import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import "../components/css/CustomerAssets.css";
import Header from "../Home/Header";
import Footer from "../Home/Footer";
import TicketSidebar from "../components/TicketSidebar";

axios.defaults.withCredentials = true;

const ITEMS_PER_PAGE = 40;
const CustomerAssets = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedType, setSelectedType] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [confirmPopup, setConfirmPopup] = useState(false);

  const [popupAlert, setPopupAlert] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  const openConfirmPopup = () => {
    if (selectedAssetIds.length === 0) {
      openPopupAlert(
        "error",
        "กรุณาเลือก Asset",
        "กรุณาเลือก Asset ที่ต้องการต่อ MA",
      );
      return;
    }
    setConfirmPopup(true);
  };

  const closeConfirmPopup = () => {
    setConfirmPopup(false);
  };

  const openPopupAlert = (type, title, message) => {
    setPopupAlert({
      show: true,
      type,
      title,
      message,
    });
  };

  const closePopupAlert = () => {
    setPopupAlert({
      show: false,
      type: "",
      title: "",
      message: "",
    });
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    const keyword = search.toLowerCase().trim();

    const filtered = rows.filter((item) => {
      const serialMatches = String(item.serial || "")
        .toLowerCase()
        .includes(keyword);

      const itemType = String(item.type || "")
        .trim()
        .toLowerCase();

      const typeMatches =
        selectedType === "all" || itemType === selectedType.toLowerCase();

      return serialMatches && typeMatches;
    });

    setFilteredRows(filtered);
    setCurrentPage(1);
  }, [search, selectedType, rows]);

  const handleSelectAsset = (assetId) => {
    setSelectedAssetIds((prev) => {
      if (prev.includes(assetId)) {
        return prev.filter((id) => id !== assetId);
      }
      return [...prev, assetId];
    });
  };

  const handleSelectAllCurrentPage = () => {
    const currentPageIds = paginatedRows.map((item) => item.id);

    const isAllSelected = currentPageIds.every((id) =>
      selectedAssetIds.includes(id),
    );

    if (isAllSelected) {
      setSelectedAssetIds((prev) =>
        prev.filter((id) => !currentPageIds.includes(id)),
      );
    } else {
      setSelectedAssetIds((prev) => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  const fetchAssets = async () => {
    try {
      const res = await axios.get("/api/customer-assets", {
        withCredentials: true,
      });

      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setFilteredRows(data);
    } catch (err) {
      console.error("fetchAssets error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "ไม่สามารถดึงข้อมูล assets ได้",
      );
      setRows([]);
      setFilteredRows([]);
    }
  };

  const handleOpenDetail = (item) => {
    navigate(`/customer/assets-list/${item.id}`, {
      state: { asset: item },
    });
  };

  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  const getSelectedAssets = () => {
    return rows.filter((item) => selectedAssetIds.includes(item.id)); // ดึงข้อมูล asset ที่ถูกเลือกโดยใช้ selectedAssetIds
  };

  const exportRenewalExcel = (assets) => {
    if (!assets || assets.length === 0) {
      alert("กรุณาเลือก Asset ที่ต้องการต่อ MA");
      return;
    }

    const excelData = assets.map((item, index) => ({
      No: index + 1,
      Customer_Name: item.customer_name || "-",
      Company_Name: item.company_name || "-",
      Email: item.customer_email || "-",
      Phone: item.customer_phone || "-",
      Location: item.location || "-",
      Serial_Number: item.serial || "-",
      Product: item.product || "-",
      Current_MA_Type: item.current_ma_type || "-",
      Expiry_Date: item.current_end_date || "-",
      Status: item.ma_status || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData); //แปลงข้อมูลเป็น worksheet
    const workbook = XLSX.utils.book_new(); //สร้าง workbook ใหม่

    XLSX.utils.book_append_sheet(workbook, worksheet, "MA Renewal Request"); //เพิ่ม worksheet ลงใน workbook

    XLSX.writeFile(workbook, "MA_Renewal_Request.xlsx"); //ดาวน์โหลดไฟล์ Excel
  };

  const handleRequestRenewal = async () => {
    const selectedAssets = getSelectedAssets();

    if (selectedAssets.length === 0) {
      alert("กรุณาเลือก Asset ที่ต้องการต่อ MA");
      return;
    }
    setConfirmPopup(false);

    const renewalItems = selectedAssets.map((item) => ({
      customer_name: item.customer_name || "",
      company_name: item.company_name || "",
      customer_email: item.customer_email || "",
      customer_phone: item.customer_phone || "",
      location: item.location || "",
      serial_number: item.serial || "",
      product: item.product || "",
      current_ma_type: item.current_ma_type || "",
      expiry_date: item.current_end_date || "",
      status: item.ma_status || "",
    }));

    try {
      await axios.post(
        "/api/customer-assets/renewal-request",
        {
          assets: renewalItems,
        },
        {
          withCredentials: true,
        },
      );

      exportRenewalExcel(selectedAssets);  

      openPopupAlert(
        "success",
        "Request Submitted",
        "ส่งคำขอต่อ MA เรียบร้อยแล้ว และดาวน์โหลดไฟล์ Excel สำเร็จ",
      );
      setSelectedAssetIds([]);
    } catch (err) {
      console.error("renewal request error:", err);

      openPopupAlert(
        "error",
        "Request Failed",
        err.response?.data?.error ||
          err.message ||
          "ไม่สามารถส่งคำขอต่อ MA ได้",
      );
    }
  };

  return (
    <>
      <Header />

      <div className="ca-layout">
        <div className="ca-sidebar-fixed">
          <TicketSidebar active="/customer/assets-list" />
        </div>

        <div className="ca-page">
          <div className="ca-card">
            <div className="ca-header">
              <div className="ca-header-top">
                <div className="ca-title-wrap">
                  <h2 className="ca-title">Assets</h2>

                  <span className="ca-total-count">
                    ({filteredRows.length})
                  </span>
                </div>

                <button
                  type="button"
                  className="ca-renewal-btn"
                  disabled={selectedAssetIds.length === 0}
                  onClick={openConfirmPopup}
                >
                  Request Renewal ({selectedAssetIds.length})
                </button>
              </div>

              <div className="ca-header-filters">
                <div className="ca-type-filter-wrap">
                  <label htmlFor="asset-type-filter">Asset Type</label>

                  <select
                    id="asset-type-filter"
                    className="ca-type-filter"
                    value={selectedType}
                    onChange={(e) => {
                      setSelectedType(e.target.value);
                      setSelectedAssetIds([]);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">All Assets</option>
                    <option value="Software">Software</option>
                    <option value="Hardware">Hardware</option>
                  </select>
                </div>

                <input
                  className="ca-search"
                  type="text"
                  placeholder="Search serial..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            {error && <div className="ca-alert ca-alert-error">{error}</div>}

            <div className="ca-table-wrap">
              <table className="ca-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={
                          paginatedRows.length > 0 &&
                          paginatedRows.every((item) =>
                            selectedAssetIds.includes(item.id),
                          )
                        }
                        onChange={handleSelectAllCurrentPage}
                      />
                    </th>
                    <th>Serial Number</th>
                    <th>Product</th>
                    <th>Current MA Type</th>
                    <th>Service Expiry Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedAssetIds.includes(item.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectAsset(item.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>
                          {item.serial ? (
                            <a
                              href={`/customer/assets-list/${item.id}`}
                              className="ca-serial-link"
                              onClick={(e) => {
                                e.preventDefault();
                                handleOpenDetail(item);
                              }}
                            >
                              {item.serial}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{item.product || "-"}</td>
                        <td>{item.current_ma_type || "-"}</td>
                        <td
                          className={
                            item.is_expiring_soon ? "text-red-soon" : ""
                          }
                        >
                          <div>{item.current_end_date || "-"}</div>

                          {item.is_expiring_soon === 1 && (
                            <div className="expiry-warning">
                              (บริการใกล้หมดอายุ)
                            </div>
                          )}
                        </td>
                        <td>
                          <span
                            className={
                              item.ma_status === "Active"
                                ? "ca-status ca-status-active"
                                : "ca-status ca-status-expired"
                            }
                          >
                            {item.ma_status || "-"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="ca-empty">
                        ไม่พบข้อมูล
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="ticket-pagination">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={currentPage === page ? "active-page" : ""}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {popupAlert.show && (
        <div className="ca-renew-popup-overlay">
          <div className="ca-renew-popup-card">
            <div
              className={
                popupAlert.type === "success"
                  ? "ca-renew-popup-icon success"
                  : "ca-renew-popup-icon error"
              }
            >
              {popupAlert.type === "success" ? "✓" : "!"}
            </div>
            <h3>{popupAlert.title}</h3>
            <p>{popupAlert.message}</p>

            <button
              type="button"
              className={
                popupAlert.type === "success"
                  ? "ca-renew-popup-ok success"
                  : "ca-renew-popup-ok error"
              }
              onClick={closePopupAlert}
            >
              OK
            </button>
          </div>
        </div>
      )}
      {confirmPopup && (
        <div className="ca-renew-popup-overlay">
          <div className="ca-renew-confirm-card">
            <div className="ca-renew-confirm-icon">?</div>

            <h3>Confirm Renewal Request</h3>

            <p>
              ต้องการส่งคำขอต่อ MA จำนวน {selectedAssetIds.length}{" "}
              รายการใช่หรือไม่?
            </p>

            <div className="ca-renew-confirm-actions">
              <button
                type="button"
                className="ca-renew-confirm-btn cancel"
                onClick={closeConfirmPopup}
              >
                Cancel
              </button>

              <button
                type="button"
                className="ca-renew-confirm-btn confirm"
                onClick={handleRequestRenewal}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default CustomerAssets;
